/**
 * /crm/configuracion/email · Gestión de cuenta(s) IMAP/SMTP.
 *
 * Permite a admin/master crear, probar, sincronizar y eliminar la cuenta
 * de correo del CRM (Fase 1: una cuenta).
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Mail, Trash2, CheckCircle2, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/lib/api';

export default function ConfiguracionEmail() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    label: 'Correo principal',
    email: '',
    imap_host: '',
    imap_port: 993,
    imap_ssl: true,
    smtp_host: '',
    smtp_port: 465,
    smtp_ssl: true,
    user: '',
    password: '',
    signature_html: '',
  });
  const [saving, setSaving] = useState(false);

  const cargar = () => API.get('/email/accounts').then(r => setAccounts(r.data?.accounts || [])).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const handleAutoFillUser = (email) => {
    setForm(f => ({ ...f, email, user: f.user || email }));
  };

  const handleGuardar = async () => {
    if (!form.email || !form.imap_host || !form.smtp_host || !form.password) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    setSaving(true);
    try {
      await API.post('/email/accounts', form);
      toast.success('Cuenta creada · pulsa "Probar conexión" para validarla');
      setForm({
        label: 'Correo principal',
        email: '', imap_host: '', imap_port: 993, imap_ssl: true,
        smtp_host: '', smtp_port: 465, smtp_ssl: true,
        user: '', password: '', signature_html: '',
      });
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (id) => {
    const t = toast.loading('Conectando IMAP…');
    try {
      const { data } = await API.post(`/email/accounts/${id}/test`);
      toast.success(`IMAP OK · ${data.folders.length} carpeta(s)`, { id: t });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error conexión', { id: t });
    }
  };

  const handleSync = async (id) => {
    const t = toast.loading('Sincronizando…');
    try {
      const { data } = await API.post(`/email/accounts/${id}/sync`);
      toast.success(`${data.nuevos} mensaje(s) nuevo(s)`, { id: t });
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error sincronizando', { id: t });
    }
  };

  const handleBorrar = async (id, email) => {
    if (!window.confirm(`¿Borrar la cuenta ${email}? También se eliminarán los correos sincronizados.`)) return;
    try {
      await API.delete(`/email/accounts/${id}`);
      toast.success('Cuenta eliminada');
      cargar();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-4xl" data-testid="config-email-page">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Mail className="w-6 h-6 text-primary" />
        Configuración de Correo
      </h1>

      {/* Listado de cuentas */}
      {accounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cuentas configuradas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {accounts.map(a => (
              <div key={a.id} className="flex items-center justify-between gap-3 p-3 border rounded-lg flex-wrap" data-testid={`account-row-${a.id}`}>
                <div>
                  <p className="font-semibold">{a.label} <Badge variant="outline" className="ml-1">{a.email}</Badge></p>
                  <p className="text-xs text-muted-foreground mt-1">
                    IMAP: {a.imap_host}:{a.imap_port} · SMTP: {a.smtp_host}:{a.smtp_port}
                    {a.last_sync_at && <> · última sync: {new Date(a.last_sync_at).toLocaleString('es-ES')}</>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleTest(a.id)} data-testid={`test-btn-${a.id}`}>
                    <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Probar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleSync(a.id)} data-testid={`sync-btn-${a.id}`}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sincronizar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleBorrar(a.id, a.email)}>
                    <Trash2 className="w-3.5 h-3.5 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Nueva cuenta */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Añadir cuenta de correo</CardTitle>
          <CardDescription className="text-xs">
            Las credenciales se guardan cifradas. Tras crear la cuenta, pulsa "Probar conexión" para validarla y luego "Sincronizar".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Etiqueta interna</Label>
              <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                placeholder="help@revix.es"
                value={form.email}
                onChange={e => handleAutoFillUser(e.target.value)}
                data-testid="form-email"
              />
            </div>
            <div>
              <Label className="text-xs">Usuario IMAP/SMTP (suele ser el email)</Label>
              <Input value={form.user} onChange={e => setForm(f => ({ ...f, user: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Contraseña *</Label>
              <Input
                type="password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                data-testid="form-password"
              />
            </div>
          </div>

          <fieldset className="border rounded-lg p-3 space-y-3">
            <legend className="text-xs font-semibold px-1 text-muted-foreground">IMAP (entrada)</legend>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Host *</Label>
                <Input placeholder="imap.revix.es" value={form.imap_host} onChange={e => setForm(f => ({ ...f, imap_host: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Puerto</Label>
                <Input type="number" value={form.imap_port} onChange={e => setForm(f => ({ ...f, imap_port: +e.target.value }))} />
              </div>
              <div className="flex items-center gap-2 mt-5">
                <Switch checked={form.imap_ssl} onCheckedChange={(v) => setForm(f => ({ ...f, imap_ssl: v }))} />
                <Label className="text-xs">SSL/TLS</Label>
              </div>
            </div>
          </fieldset>

          <fieldset className="border rounded-lg p-3 space-y-3">
            <legend className="text-xs font-semibold px-1 text-muted-foreground">SMTP (salida)</legend>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Host *</Label>
                <Input placeholder="smtp.revix.es" value={form.smtp_host} onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Puerto</Label>
                <Input type="number" value={form.smtp_port} onChange={e => setForm(f => ({ ...f, smtp_port: +e.target.value }))} />
              </div>
              <div className="flex items-center gap-2 mt-5">
                <Switch checked={form.smtp_ssl} onCheckedChange={(v) => setForm(f => ({ ...f, smtp_ssl: v }))} />
                <Label className="text-xs">SSL/TLS</Label>
              </div>
            </div>
          </fieldset>

          <div>
            <Label className="text-xs">Firma HTML (opcional)</Label>
            <Textarea
              rows={4}
              value={form.signature_html}
              onChange={e => setForm(f => ({ ...f, signature_html: e.target.value }))}
              placeholder="Pega aquí el HTML de tu firma corporativa"
            />
          </div>

          <Button onClick={handleGuardar} disabled={saving} data-testid="form-submit">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {saving ? 'Guardando…' : 'Guardar cuenta'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
